import { Patch } from './types';

export type JsonPatchOperation =
  | { op: 'add'; path: string; value: any }
  | { op: 'remove'; path: string };

export function escapeJsonPointerSegment(value: string): string {
  return value.replace(/~/g, '~0').replace(/\//g, '~1');
}

function isPlainObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

function jsonValuesEqual(left: any, right: any): boolean {
  if (left === right) return true;
  return JSON.stringify(left) === JSON.stringify(right);
}

function appendJsonPatchOperations(
  operations: JsonPatchOperation[],
  currentValue: any,
  patchValue: any,
  path: string,
): void {
  if (patchValue === undefined) {
    if (currentValue !== undefined) operations.push({ op: 'remove', path });
    return;
  }

  if (isPlainObject(patchValue)) {
    if (!isPlainObject(currentValue)) {
      operations.push({ op: 'add', path, value: patchValue });
      return;
    }

    Object.entries(patchValue).forEach(([key, value]) => {
      appendJsonPatchOperations(
        operations,
        currentValue[key],
        value,
        `${path}/${escapeJsonPointerSegment(key)}`,
      );
    });
    return;
  }

  if (!jsonValuesEqual(currentValue, patchValue)) {
    // RFC 6902 "add" also replaces an existing object member.
    operations.push({ op: 'add', path, value: patchValue });
  }
}

export function createJsonPatch<T extends object>(currentValue: T, valuePatch: Patch<T>): JsonPatchOperation[] {
  const operations: JsonPatchOperation[] = [];
  const currentObject = currentValue as Record<string, any>;

  Object.entries(valuePatch as Record<string, any>).forEach(([key, value]) => {
    appendJsonPatchOperations(
      operations,
      currentObject[key],
      value,
      `/${escapeJsonPointerSegment(key)}`,
    );
  });

  return operations;
}

export function applyValuePatch<T>(currentValue: T, valuePatch: Patch<T>): T {
  if (!isPlainObject(currentValue) || !isPlainObject(valuePatch)) return valuePatch as T;

  const patchedValue: Record<string, any> = { ...currentValue };
  Object.entries(valuePatch as Record<string, any>).forEach(([key, value]) => {
    if (value === undefined) {
      delete patchedValue[key];
    } else if (isPlainObject(value) && isPlainObject(patchedValue[key])) {
      patchedValue[key] = applyValuePatch(patchedValue[key], value);
    } else {
      patchedValue[key] = value;
    }
  });

  return patchedValue as T;
}
